import axios from 'axios'

// Use production URL when not on localhost, otherwise use local dev server
const isProduction = typeof window !== 'undefined' && !window.location.hostname.includes('localhost')
const API_BASE_URL = isProduction
  ? 'https://api.splitmytransaction.com'
  : 'http://localhost:5001'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Track if we're currently refreshing to avoid multiple refresh attempts
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors and refresh tokens
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // If 401 and we haven't already tried to refresh for this request
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Short-circuit Monarch auth errors — don't waste a JWT refresh
      if (error.response?.data?.code === 'MONARCH_AUTH_REQUIRED') {
        window.dispatchEvent(new CustomEvent('monarch-auth-required'))
        return Promise.reject(error)
      }

      // Don't try to refresh if this IS the refresh request
      if (originalRequest.url === '/auth/refresh-jwt') {
        localStorage.removeItem('token')
        window.location.href = '/login'
        return Promise.reject(error)
      }

      if (isRefreshing) {
        // Another refresh is in progress, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return api(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Attempt to refresh the JWT token
        const response = await api.post('/auth/refresh-jwt')
        const newToken = response.data.token

        // Store the new token
        localStorage.setItem('token', newToken)

        isRefreshing = false
        processQueue(null, newToken)

        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return api(originalRequest)
      } catch (refreshError) {
        // Refresh failed - clear token and redirect to login
        isRefreshing = false
        processQueue(refreshError, null)
        localStorage.removeItem('token')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

// Auth endpoints
export const authAPI = {
  login: () => api.get('/auth/login'),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  refreshToken: () => api.post('/auth/refresh'),
  refreshJwt: () => api.post('/auth/refresh-jwt'),
  getPreferences: () => api.get('/auth/preferences'),
  updatePreferences: (data) => api.put('/auth/preferences', data)
}

// Transaction endpoints
export const transactionsAPI = {
  getAll: (params) => api.get('/api/transactions', { params }),
  getMatched: (params) => api.get('/api/transactions/matched', { params }),
  getReviewQueue: () => api.get('/api/transactions/review-queue'),
  getNextReview: (currentId) => api.get('/api/transactions/next-review', { params: { current_id: currentId } }),
  autoMatch: () => api.post('/api/transactions/auto-match'),
  getById: (id, params) => api.get(`/api/transactions/${id}`, { params }),
  get: (id, params) => api.get(`/api/transactions/${id}`, { params }),
  update: (id, data) => api.put(`/api/transactions/${id}`, data),
  confirm: (id) => api.post(`/api/transactions/${id}/confirm`),
  acceptSuggestions: (id) => api.post(`/api/transactions/${id}/accept-suggestions`),
  reset: (id) => api.post(`/api/transactions/${id}/reset`),
  reparse: (id) => api.post(`/api/transactions/${id}/reparse`),
  dismiss: (id) => api.post(`/api/transactions/${id}/dismiss`),
  restore: (id) => api.post(`/api/transactions/${id}/restore`),
  delete: (id) => api.delete(`/api/transactions/${id}`),
  getCleanedEmail: (id) => api.get(`/api/transactions/${id}/cleaned-email`),
  updateItem: (transactionId, itemId, data) =>
    api.put(`/api/transactions/${transactionId}/items/${itemId}`, data),
  deleteItem: (transactionId, itemId) =>
    api.delete(`/api/transactions/${transactionId}/items/${itemId}`)
}

// Category endpoints
export const categoriesAPI = {
  getAll: (params) => api.get('/api/categories', { params }),
  getById: (id) => api.get(`/api/categories/${id}`),
  sync: () => api.post('/api/categories/sync')
}

// Email forwarding endpoints
export const forwardingAPI = {
  getSetupInstructions: () => api.get('/api/forwarding/setup'),
  getStatus: () => api.get('/api/forwarding/status'),
  getPendingConfirmation: () => api.get('/api/forwarding/pending-confirmation'),
  confirmVerified: () => api.post('/api/forwarding/confirm-verified'),
  reset: () => api.post('/api/forwarding/reset'),
  // Used by ForwardingSetupView
  initiateSetup: () => api.get('/api/forwarding/setup'),
  verify: (code) => api.post('/api/forwarding/verify', { confirmation_code: code })
}

// Household endpoints
export const householdAPI = {
  get: () => api.get('/api/household'),
  update: (data) => api.put('/api/household', data),
  delete: () => api.delete('/api/household'),
  // Members
  getMembers: () => api.get('/api/household/members'),
  inviteMemberByEmail: (email) => api.post('/api/household/members', { email }),
  removeMember: (userId) => api.delete(`/api/household/members/${userId}`),
  leave: () => api.post('/api/household/leave'),
  // Pending invites (invites sent TO the current user)
  getPendingInvites: () => api.get('/api/household/pending-invites'),
  acceptPendingInvite: (inviteId) => api.post(`/api/household/pending-invites/${inviteId}/accept`),
  declinePendingInvite: (inviteId) => api.post(`/api/household/pending-invites/${inviteId}/decline`),
  // Invites (invites sent BY the current user)
  createInvite: (data) => api.post('/api/household/invites', data),
  listInvites: () => api.get('/api/household/invites'),
  revokeInvite: (token) => api.delete(`/api/household/invites/${token}`),
  getInviteInfo: (token) => api.get(`/api/household/invites/${token}/info`),
  acceptInvite: (token) => api.post(`/api/household/join/${token}`)
}

// Sync endpoints
export const syncAPI = {
  // General status
  getStatus: () => api.get('/api/sync/status'),
  updateSettings: (data) => api.put('/api/sync/settings', data),
  // Monarch endpoints
  monarchLogin: (credentials) => api.post('/api/sync/monarch/login', credentials),
  getMonarchStatus: () => api.get('/api/sync/monarch/status'),
  getMonarchTransactions: (params) => api.get('/api/sync/monarch/transactions', { params }),
  syncMonarchTransactions: (data) => api.post('/api/sync/monarch/transactions/sync', data),
  getMonarchTransactionSplits: (transactionId) => api.get(`/api/sync/monarch/transactions/${transactionId}/splits`),
  getMonarchCategories: () => api.get('/api/sync/monarch/categories'),
  syncMonarchBudget: () => api.post('/api/sync/monarch/budget/sync'),
  monarchDisconnect: () => api.post('/api/sync/monarch/disconnect')
}

export default api
